import React from 'react';
import { IconButton, Tooltip } from '@mui/material';
import MeetingRoomOutlinedIcon from '@mui/icons-material/MeetingRoomOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import PowerSettingsNewOutlinedIcon from '@mui/icons-material/PowerSettingsNewOutlined';
import RestoreOutlinedIcon from '@mui/icons-material/RestoreOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import styles from './RoomListCard.module.css';

const peopleLabel = (count) => `${count} ${Number(count) === 1 ? 'pessoa' : 'pessoas'}`;

/** Ação compacta da linha: ícone com tooltip (o span mantém o tooltip no botão desligado). */
function RowAction({ title, label, disabled, onClick, Icon, tone }) {
    return (
        <Tooltip title={title} arrow>
            <span>
                <IconButton
                    size="small"
                    className={`${styles.rowAction} ${tone ? styles[tone] : ''}`}
                    onClick={onClick}
                    disabled={disabled}
                    aria-label={label}
                >
                    <Icon fontSize="small" />
                </IconButton>
            </span>
        </Tooltip>
    );
}

/**
 * Selo com os atendimentos de hoje na sala. A capacidade é de gente ao mesmo
 * tempo, então o total do dia não é comparado com ela (fica só na dica).
 */
function OccupancyTag({ occupancy }) {
    if (!occupancy) return null;
    const count = Number(occupancy.current_occupancy) || 0;
    const label = count === 0
        ? 'Hoje: nenhum atendimento'
        : `Hoje: ${count} ${count === 1 ? 'atendimento' : 'atendimentos'}`;
    const tone = count === 0 ? styles.tagInactive : styles.occupancyLow;

    return (
        <Tooltip title={`Atendimentos de hoje nesta sala. Cabem até ${peopleLabel(occupancy.capacity)} ao mesmo tempo.`} arrow>
            <span className={`${styles.tag} ${tone}`}>
                <span className={styles.dot} />
                {label}
            </span>
        </Tooltip>
    );
}

/**
 * Uma sala: ícone, nome e descrição, capacidade, ocupação, situação e as ações
 * (editar, ativar/inativar, excluir), só para quem pode editar.
 */
function RoomRow({ room, occupancy, canEdit, reactivateHint, busy, onEdit, onToggle, onDelete }) {
    const inactive = !room.is_active;

    return (
        <li className={`${styles.row} ${inactive ? styles.muted : ''}`}>
            <span className={`${styles.roomIcon} ${inactive ? styles.roomIconMuted : ''}`}>
                <MeetingRoomOutlinedIcon fontSize="small" />
            </span>

            <div className={styles.identity}>
                <span className={`${styles.name} ${styles.ellipsis}`}>{room.name}</span>
                <span className={`${styles.secondary} ${styles.ellipsis}`} title={room.description || undefined}>
                    {room.description || 'Sem descrição'}
                </span>
            </div>

            <span className={styles.detail}>
                <GroupsOutlinedIcon className={styles.detailIcon} />
                Até {peopleLabel(room.capacity)}
            </span>

            <div className={styles.tags}>
                <OccupancyTag occupancy={occupancy} />
                {inactive
                    ? <span className={`${styles.tag} ${styles.tagInactive}`}>Inativa</span>
                    : <span className={`${styles.tag} ${styles.tagActive}`}>Ativa</span>}
            </div>

            {canEdit && (
                <span className={styles.actions}>
                    <RowAction
                        title="Editar sala"
                        label={`Editar ${room.name}`}
                        disabled={busy}
                        onClick={() => onEdit(room)}
                        Icon={EditOutlinedIcon}
                    />
                    {inactive ? (
                        <RowAction
                            title={reactivateHint || 'Ativar sala'}
                            label={`Ativar ${room.name}`}
                            disabled={busy}
                            onClick={() => onToggle(room)}
                            Icon={RestoreOutlinedIcon}
                            tone="positive"
                        />
                    ) : (
                        <RowAction
                            title="Inativar sala"
                            label={`Inativar ${room.name}`}
                            disabled={busy}
                            onClick={() => onToggle(room)}
                            Icon={PowerSettingsNewOutlinedIcon}
                            tone="warning"
                        />
                    )}
                    <RowAction
                        title="Excluir sala"
                        label={`Excluir ${room.name}`}
                        disabled={busy}
                        onClick={() => onDelete(room)}
                        Icon={DeleteOutlinedIcon}
                        tone="danger"
                    />
                </span>
            )}
        </li>
    );
}

export default RoomRow;
